import {
    Badge,
    Group,
    Stack,
    Text,
} from '@mantine/core';
import {
    PlatformPropertyCard,
    PlatformPropertyCardEntryInput,
} from '@flexinet/ui-components';

import {Persona} from '../types';
import {useTestDefinitionsQuery} from '../../testing-test-definitions/hooks/useTestDefinitionsQuery';

export const Profile = ({record}: { record: Persona }) => {

    const actionCodes = record.actionCodes ?? [];
    const testDefinitionIds = (record.testDefinitionIds ?? [])
        .filter((id): id is string => typeof id === 'string');

    // Resolve ids to test codes so the profile reads as something a human
    // recognises. An id with no matching definition still renders — as the raw
    // id — rather than disappearing, so a dangling reference stays visible.
    const {data: definitions = []} = useTestDefinitionsQuery();
    const labelForId = (id: string) => {
        const match = definitions.find(definition => definition.id === id);
        return match ? match.testCode : id;
    };

    const properties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Name',
            value: record.name,
            span: 12,
        }, {
            label: 'Description',
            value: record.description ?? 'n/a',
            span: 12,
        }, {
            // What the executor actually runs.
            label: 'Test Definitions',
            value: testDefinitionIds.length > 0 ? (
                <Group gap={'xs'}>
                    {testDefinitionIds.map(id => (
                        <Badge key={id} variant={'light'}>
                            {labelForId(id)}
                        </Badge>
                    ))}
                </Group>
            ) : (
                <Text size={'sm'} c={'dimmed'}>Not set</Text>
            ),
            span: 12,
        }, {
            // DEPRECATED — retained through the FNP-968 transition.
            label: 'Action Codes (deprecated)',
            value: actionCodes.length > 0 ? (
                <Group gap={'xs'}>
                    {actionCodes.map(entry => (
                        <Badge key={entry.actionCode} variant={'light'}>
                            {entry.actionCode} ({entry.weight})
                        </Badge>
                    ))}
                </Group>
            ) : (
                <Text size={'sm'} c={'dimmed'}>Not set</Text>
            ),
            span: 12,
        }];

    // lifecycle is nullable — render each field as "Not set" rather than hiding
    // the section, so it is obvious the persona still needs configuring.
    const lifecycleProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Wake Cadence (hours)',
            value: record.lifecycle ? String(record.lifecycle.wakeCadenceHours) : 'Not set',
            span: 12,
        }, {
            label: 'Expected Lifespan (days)',
            value: record.lifecycle ? String(record.lifecycle.expectedLifespanDays) : 'Not set',
            span: 12,
        }, {
            label: 'Cancellation Likelihood (%)',
            value: record.lifecycle ? String(record.lifecycle.cancellationLikelihoodPercent) : 'Not set',
            span: 12,
        }];

    const auditProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Created By',
            value: record.createdByUsername ?? 'n/a',
            span: 12,
        }, {
            label: 'Created',
            value: record.created,
            formatter: 'timestamp',
            span: 12,
        }, {
            label: 'Last Updated',
            value: record.updated,
            formatter: 'timestamp',
            span: 12,
        }];

    return (
        <Stack gap={'xl'}>
            <PlatformPropertyCard properties={properties}/>
            <PlatformPropertyCard properties={lifecycleProperties}/>
            <PlatformPropertyCard properties={auditProperties}/>
        </Stack>
    );
};
